import { useContext, useState } from 'react';
import { CheckoutStatus } from '../../models/CheckoutSession';
import { OtpRequest } from '../../models/Otp';
import { PaymentResult } from '../../models/Payment';
import { cartItemData } from '../../redux/actions/cartActions';
import { aetherApi } from '../../redux/api/aetherApi';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updatePaymentRequests, clearPaymentRequests } from '../../redux/reducers/checkoutReducer';
import {
  selectParams,
  selectSessionId,
  selectStatus,
  selectPaymentRequests,
} from '../../redux/selectors/checkoutSelectors';
import { getCartById } from '../../util/cart/cartAPIUtil';
import { useSubmitPaymentsMutation, useSubmitOrderMutation } from '../../redux/api/checkoutApi';
import { PaymentMethodType } from '../../models/PaymentMethodType';
import { AuthContext } from '../../common/Auth';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export default function usePlaceOrder({
  selected,
  setSelected,
  setOpenOtpDialog,
}: {
  selected?: number | null;
  setSelected?: (value: number | null) => void;
  setOpenOtpDialog?: (value: boolean) => void;
}) {
  const { user, ipV4, getUserPoints } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const sessionId = useAppSelector((state) => selectSessionId(state, params));
  const status = useAppSelector((state) => selectStatus(state, params));
  const [loadingPlaceOrder, setLoadingPlaceOrder] = useState<boolean>(false);
  const paymentRequests = useAppSelector(selectPaymentRequests);

  const [submitPayments, { isLoading: submitPaymentsLoading, error: submitPaymentsError }] =
    useSubmitPaymentsMutation();
  const [submitOrder, { isLoading: submitOrderLoading, error: submitOrderError }] =
    useSubmitOrderMutation();

  const [paymentErrors, setPaymentErrors] = useState<
    { method: PaymentMethodType; message: string }[]
  >([]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const placeOrder = async (otpReq?: OtpRequest) => {
    try {
      if (!sessionId || !params) {
        throw new Error('Session ID or user ID not found');
      }
      setLoadingPlaceOrder(true);
      const newPaymentRequests = [];
      const newPaymentErrors = [];
      let submit = true;
      if (status === CheckoutStatus.PENDING_PAYMENT) {
        let reCaptchaToken = undefined;
        if (executeRecaptcha) {
          try {
            reCaptchaToken = await executeRecaptcha();
          } catch (error) {
            console.error('Error executing reCaptcha', error);
          }
        }
        if (submitPaymentsLoading) {
          throw new Error('Payments already being submitted');
        }
        const { paymentResponses, session } = await submitPayments({
          sessionId,
          userId: params.userId,
          paymentRequests,
          otpReq,
          reCaptchaToken,
        }).unwrap();
        if (user) {
          dispatch(aetherApi.util.invalidateTags(['BudgetHistory', 'Budgets', 'Vouchers']));
          getUserPoints();
        }
        if (session.status === CheckoutStatus.PENDING_PAYMENT) {
          // Update form with failed payments
          if (paymentResponses.length !== paymentRequests.length) {
            throw new Error('Payment response count does not match request count');
          }
          for (let i = 0; i < paymentResponses.length; i++) {
            const response = paymentResponses[i];
            if (response.result !== PaymentResult.SUCCESS) {
              newPaymentRequests.push(paymentRequests[i]);
              if (
                response.result === PaymentResult.FAILURE ||
                response.result === PaymentResult.REVERTED
              ) {
                newPaymentErrors.push({
                  method: response.methodType,
                  message: response.message ?? 'Unknown error processing payment',
                });
                if (selected === null) {
                  setSelected?.(i);
                }
              }
            }
          }
          dispatch(updatePaymentRequests(newPaymentRequests));
          setPaymentErrors(newPaymentErrors);
          submit = false;
        } else if (session.status === CheckoutStatus.COMPLETED) {
          submit = false;
          dispatch(clearPaymentRequests());
          setPaymentErrors([]);
          const cartData = await getCartById(user, ipV4);
          dispatch(cartItemData(cartData ?? { _id: null, cartData: [] }));
        }
      }
      if (submit && params) {
        dispatch(clearPaymentRequests());
        setPaymentErrors([]);
        await submitOrder({ sessionId, userId: params.userId }).unwrap();
        const cartData = await getCartById(user, ipV4);
        dispatch(cartItemData(cartData ?? { _id: null, cartData: [] }));
      }
    } catch (error: any) {
      if (error?.data?.message === 'OTP Required') {
        setOpenOtpDialog?.(true);
        return;
      }
      console.error(error);
    } finally {
      setLoadingPlaceOrder(false);
    }
  };

  return {
    loadingPlaceOrder,
    placeOrder,
    submitPaymentsLoading,
    submitPaymentsError,
    submitOrderLoading,
    submitOrderError,
    paymentErrors,
  };
}
